import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSpacePath } from '../../../hooks/useSpacePath';
import { usePermissions } from '../../../hooks/usePermissions';
import { useGetQuoteColumns } from '../api/useGetQuoteColumns';
import { useGetQuoteGroup } from '../api/useGetQuoteGroup';
import { useGetQuotes } from '../api/useGetQuotes';

function plural(count: number, singular: string, pluralForm: string) {
  return `${count} ${count > 1 ? pluralForm : singular}`;
}

export function useQuoteGroupPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const spacePath = useSpacePath();
  const [isQuoteFormOpen, setIsQuoteFormOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);

  const groupQuery = useGetQuoteGroup(groupId);
  const columnsQuery = useGetQuoteColumns(groupId);
  const quotesQuery = useGetQuotes(groupId);

  const group = groupQuery.data ?? null;
  const columns = columnsQuery.data ?? [];
  const quotes = quotesQuery.data ?? [];
  const visibleColumnCount = columns.filter((column) => column.visible).length;
  const listPath = spacePath('/quotes');

  function handleRetry() {
    groupQuery.refetch();
    columnsQuery.refetch();
    quotesQuery.refetch();
  }

  return {
    groupId: groupId ?? '',
    isAdmin,
    group,
    columns,
    quotes,
    visibleColumnCount,
    listPath,
    isGroupLoading: groupQuery.isPending,
    isContentLoading: columnsQuery.isPending || quotesQuery.isPending,
    isError: groupQuery.isError || columnsQuery.isError || quotesQuery.isError,
    isNotFound: groupQuery.isSuccess && !group,
    subtitle: group
      ? `${plural(quotes.length, 'devis', 'devis')} · ${plural(visibleColumnCount, 'critère affiché', 'critères affichés')}`
      : undefined,
    isQuoteFormOpen,
    isColumnsOpen,
    handleRetry,
    handleBackToList: () => navigate(listPath),
    handleOpenQuoteForm: () => setIsQuoteFormOpen(true),
    handleCloseQuoteForm: () => setIsQuoteFormOpen(false),
    handleOpenColumns: () => setIsColumnsOpen(true),
    handleCloseColumns: () => setIsColumnsOpen(false),
  };
}
